import {
  ApplicationRef,
  Component,
  ComponentFactoryResolver,
  ComponentRef,
  EmbeddedViewRef,
  Injector,
  OnDestroy,
  OnInit,
  ViewChild
} from '@angular/core';
import { StatusType, Note, SiteTwin, Camera } from 'src/api/models';
import { ApplicationConfigService } from 'src/service/application-config.service';
import { IMultiSourceMultiValueWidget } from 'src/app/models/dashboard';
import { AzureAuthService } from 'src/service/azure-auth.service';
import { FireService } from 'src/service/fire.service';
import { MapComponent } from 'src/app/components/map/map.component';

import 'leaflet';
import { FireCameraViewer } from 'src/app/components/fire-camera-viewer/fire-camera-viewer.component';

declare let L;

@Component({
  selector: 'app-fire-foresight-cameras',
  templateUrl: './fire-foresight-cameras.component.html',
  styleUrls: ['./fire-foresight-cameras.component.css']
})
export class FireForesightCamerasComponent implements OnInit, OnDestroy {
  @ViewChild('map', { read: MapComponent, static: false }) map: MapComponent;

  public list : StatusType[];
  public siteId: string;
  public liveWidget: IMultiSourceMultiValueWidget;
  public latestImageUrl: string;
  public selectedNote: Note;
  public newNote: string;

  public currentView: 'list' | 'map' = 'list';
  private readonly VIEW_MODE_STORAGE_KEY = 'fireForesightCamerasViewMode';

  private popupViewerComponent: ComponentRef<FireCameraViewer> = null;
  private popupHostElement: HTMLElement = null;
  private markersGroup: any = null;
  private lastCameraSignature: string = '';
  private mapReady: boolean = false;

  private get cameras(): Camera[] {
    return this.fire.cameras || [];
  }

  private get shouldShowNoCamerasMessage(): boolean {
    return !this.fire.cameraLoadStarted && this.cameras.length === 0;
  }

  public get showNoCamerasListMessage(): boolean {
    return this.currentView === 'list' && this.shouldShowNoCamerasMessage;
  }

  public get showNoCamerasMapMessage(): boolean {
    return this.currentView === 'map' && this.shouldShowNoCamerasMessage;
  }

  public get hasMapCameras(): boolean {
    return this.getMapCameraSites().length > 0;
  }

  public get showNoCoordinatesMessage(): boolean {
    return this.currentView === 'map' && !this.fire.cameraLoadStarted && this.cameras.length > 0 && !this.hasMapCameras;
  }
  
  constructor(
    public app: ApplicationConfigService, 
    public security: AzureAuthService,
    public fire: FireService,
    private componentFactoryResolver: ComponentFactoryResolver,
    private appRef: ApplicationRef,
    private injector: Injector) {
    
    this.fire.get(false);
  }

  ngOnInit(): void {
    const savedView = localStorage.getItem(this.VIEW_MODE_STORAGE_KEY) as 'list' | 'map';
    if (savedView === 'list' || savedView === 'map') {
      this.currentView = savedView;
    }
  }

  ngOnDestroy(): void {
    this.clearMapMarkers();
    this.destroyPopupViewer();
  }

  ngDoCheck(): void {
    if (this.currentView !== 'map' || !this.mapReady || !this.map?.map) {
      return;
    }

    const signature = this.getCameraSignature();
    if (signature !== this.lastCameraSignature) {
      if (this.renderCameraMarkers()) {
        this.lastCameraSignature = signature;
      }
    }
  }

  setView(view: 'list' | 'map'): void {
    if (view === this.currentView) {
      return;
    }

    this.currentView = view;
    localStorage.setItem(this.VIEW_MODE_STORAGE_KEY, view);

    if (view === 'map') {
      this.mapReady = false;
      return;
    }

    this.mapReady = false;
    this.destroyPopupViewer();
  }

  onMapInitComplete(): void {
    this.mapReady = true;
    if (this.currentView === 'map') {
      if (this.renderCameraMarkers()) {
        this.lastCameraSignature = this.getCameraSignature();
      }
    }
  }

  getIcon(site: SiteTwin, pins: any[]): any {
    return { icon: pins[site.siteType] ?? pins['blueSelected'] };
  }

  private getCameraSignature(): string {
    if (!this.fire.cameras) {
      return '';
    }

    return this.fire.cameras
      .map(c => `${c.siteId}:${c.latitude ?? ''}:${c.longitude ?? ''}:${c.twin?.latitude ?? ''}:${c.twin?.longitude ?? ''}:${c.offline}`)
      .join('|');
  }

  private getMapCameraSites(): SiteTwin[] {
    const sites: SiteTwin[] = [];
    for (const camera of this.cameras) {
      const twin = camera.twin || ({} as SiteTwin);
      const lat = this.getCameraLatitude(camera);
      const lng = this.getCameraLongitude(camera);
      if (lat == null || lng == null) {
        continue;
      }

      const site: SiteTwin = {
        ...twin,
        latitude: lat,
        longitude: lng,
        siteName: twin.siteName || camera.name,
        siteDescription: camera.name,
        siteType: twin.siteType || 'Fire Camera',
        siteUri: twin.siteUri || camera.siteUri
      };

      (site as any).__camera = camera;
      sites.push(site);
    }

    return sites;
  }

  private getCameraLatitude(camera: Camera): number {
    if (camera.latitude != null && camera.latitude >= -90 && camera.latitude <= 90) {
      return camera.latitude;
    }
    if (camera.twin?.latitude != null && camera.twin.latitude >= -90 && camera.twin.latitude <= 90) {
      return camera.twin.latitude;
    }
    return null;
  }

  private getCameraLongitude(camera: Camera): number {
    if (camera.longitude != null && camera.longitude >= -180 && camera.longitude <= 180) {
      return camera.longitude;
    }
    if (camera.twin?.longitude != null && camera.twin.longitude >= -180 && camera.twin.longitude <= 180) {
      return camera.twin.longitude;
    }
    return null;
  }

  private renderCameraMarkers(): boolean {
    if (!this.map || !this.map.map || this.currentView !== 'map') {
      return false;
    }

    const sites = this.getMapCameraSites();
    if (sites.length === 0) {
      this.clearMapMarkers();
      return true;
    }

    this.destroyPopupViewer();
    this.markersGroup = this.map.displaySites(
      sites,
      this.getIcon,
      null,
      (marker, site) => this.openCameraPopup(marker, site),
      true
    );
    return true;
  }

  private openCameraPopup(marker: any, site: SiteTwin): void {
    const camera = (site as any).__camera as Camera;
    if (!camera || !camera.twin) {
      return;
    }

    this.destroyPopupViewer();

    const popupContainer = document.createElement('div');
    popupContainer.className = 'camera-map-popup-viewer';
    this.popupHostElement = popupContainer;

    const markerIcon = marker.getIcon && marker.getIcon();
    const iconHeight = (markerIcon && markerIcon.options && markerIcon.options.iconSize)
      ? markerIcon.options.iconSize[1]
      : 41;

    const popup = L.popup({
      maxWidth: 460,
      minWidth: 360,
      className: 'camera-map-popup',
      offset: L.point(0, -iconHeight)
    })
      .setLatLng(marker.getLatLng())
      .setContent(popupContainer);

    popup.on('remove', () => {
      this.destroyPopupViewer();
    });

    popup.openOn(this.map.map);

    // Mount viewer after the popup DOM node is live.
    setTimeout(() => this.mountPopupViewer(camera, popupContainer), 0);
  }

  private mountPopupViewer(camera: Camera, host: HTMLElement): void {
    this.destroyPopupViewer();

    const factory = this.componentFactoryResolver.resolveComponentFactory(FireCameraViewer);
    const componentRef = factory.create(this.injector);
    componentRef.instance.siteTwin = camera.twin;
    componentRef.instance.fullScreen = true;
    componentRef.instance.autoRotate = true;
    componentRef.instance.offline = camera.offline;

    this.appRef.attachView(componentRef.hostView);
    const element = (componentRef.hostView as EmbeddedViewRef<any>).rootNodes[0] as HTMLElement;
    host.appendChild(element);

    componentRef.changeDetectorRef.detectChanges();
    this.popupViewerComponent = componentRef;
  }

  private destroyPopupViewer(): void {
    if (!this.popupViewerComponent) {
      return;
    }

    this.appRef.detachView(this.popupViewerComponent.hostView);
    this.popupViewerComponent.destroy();
    this.popupViewerComponent = null;

    if (this.popupHostElement) {
      this.popupHostElement.innerHTML = '';
      this.popupHostElement = null;
    }
  }

  private clearMapMarkers(): void {
    try {
      if (this.map && this.map.map && this.markersGroup) {
        this.map.map.removeLayer(this.markersGroup);
      }
    } catch (ex) {
      // Ignore layer cleanup errors.
    }
    this.markersGroup = null;
  }
}